<template>
  <div class="doctor-image__frame" :style="frameStyle">
    <img
      v-if="!isFallback"
      class="doctor-image__img"
      :src="normalizedImage"
      :alt="name"
      loading="lazy"
      decoding="async"
      @error="handleImageError"
    />
    <span
      v-else
      class="doctor-image__initials"
      :style="initialsStyle"
      role="img"
      :aria-label="name"
    >
      {{ initials }}
    </span>
  </div>
</template>

<script>
// Single brand-indigo tint so avatars stay on-palette with the rest of the UI.
const AVATAR_TINT = { bg: "#EEF0FE", fg: "#3346D3" };

export default {
  props: {
    image: {
      type: String,
      default: "",
    },
    name: {
      type: String,
      required: true,
    },
    size: {
      type: [String, Number],
      default: "6.4rem",
    },
  },
  data() {
    return {
      hasError: false,
    };
  },
  computed: {
    sizeParts() {
      const raw = this.size;
      if (typeof raw === "number") {
        return { value: raw, unit: "px" };
      }
      const normalized = typeof raw === "string" ? raw.trim() : "";
      const match = normalized.match(/^([\d.]+)\s*([a-z%]*)$/i);
      if (match) {
        return { value: parseFloat(match[1]), unit: match[2] || "px" };
      }
      return { value: 6.4, unit: "rem" };
    },
    frameStyle() {
      const { value, unit } = this.sizeParts;
      const size = `${value}${unit}`;
      return {
        width: size,
        height: size,
        fontSize: `${(value * 0.4).toFixed(2)}${unit}`,
      };
    },
    normalizedImage() {
      return typeof this.image === "string" ? this.image.trim() : "";
    },
    isFallback() {
      return this.hasError || !this.normalizedImage;
    },
    initials() {
      const source = (this.name || "").trim();
      if (!source) {
        return "?";
      }
      const words = source.split(/\s+/).filter(Boolean);
      const letters = words.slice(0, 2).map((word) => word.charAt(0));
      return letters.join("").toUpperCase() || "?";
    },
    tint() {
      return AVATAR_TINT;
    },
    initialsStyle() {
      return {
        background: this.tint.bg,
        color: this.tint.fg,
      };
    },
  },
  watch: {
    image() {
      this.hasError = false;
    },
  },
  methods: {
    handleImageError() {
      this.hasError = true;
    },
  },
};
</script>

<style scoped>
.doctor-image__frame {
  border-radius: 999px;
  overflow: hidden;
  flex-shrink: 0;
}

.doctor-image__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.doctor-image__initials {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1;
  user-select: none;
}
</style>
